import { useState, useEffect } from 'react';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db, auth, signOut } from '../firebase';
import { useAuth } from '../AuthContext';
import UserTile from '../components/UserTile';
import UpdateProfile from './UpdateProfile';
import { isProfileIncomplete } from '../utils/profileUtils';
import './Dashboard.css';

const Profile = () => {
  const { currentUser } = useAuth();
  const [userProfile, setUserProfile] = useState(null);
  const [kpis, setKpis] = useState({ mealsHosted: null, mealsAttended: null, hostRating: null, guestRating: null });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }

      try {
        const userDocRef = doc(db, 'users', currentUser.uid);
        const contactDocRef = doc(db, 'users', currentUser.uid, 'private', 'contact');
        const [userDoc, contactDoc, hostedSnap, attendedSnap] = await Promise.all([
          getDoc(userDocRef),
          getDoc(contactDocRef),
          getDocs(query(collection(db, 'meals'), where('hostId', '==', currentUser.uid), where('status', '==', 'active'))),
          getDocs(query(collection(db, 'bookings'), where('guestId', '==', currentUser.uid), where('userAttendedMeal', '==', true))),
        ]);

        if (userDoc.exists()) {
          const userData = userDoc.data();
          // bio/phoneNumber (F-07) live on the private contact subdoc, not
          // userProfile -- merge them in so the rest of this screen (and
          // UpdateProfile) can keep treating userProfile as one flat object.
          setUserProfile({ ...userData.userProfile, ...(contactDoc.exists() ? contactDoc.data() : {}) });
          setKpis({
            mealsHosted: hostedSnap.size,
            mealsAttended: attendedSnap.size,
            hostRating: userData.hostRatings?.avgOverallRating ?? null,
            guestRating: userData.guestRatings?.avgOverallRating ?? null,
          });
        }
      } catch (err) {
        console.error('Error fetching user profile:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [currentUser]);

  // Show modal if profile is incomplete after loading
  useEffect(() => {
    if (!loading && isProfileIncomplete(userProfile)) {
      setShowCompleteModal(true);
    }
  }, [loading, userProfile]);

  if (!currentUser || loading) {
    return (
      <div className="dashboard-container">
        <p className="dashboard-loading">Loading your profile...</p>
      </div>
    );
  }

  const handleCompleteProfile = () => {
    setShowCompleteModal(false);
    setEditing(true);
  };

  return (
    <div className="dashboard-container">
      {showCompleteModal && (
        <div className="modal-overlay">
          <div className="complete-profile-modal">
            <div className="modal-emoji">👋</div>
            <h2 className="modal-title">Complete Your Profile!</h2>
            <p className="modal-text">
              You're almost there! Fill out your profile so others can get to know you better.
            </p>
            <div className="modal-buttons">
              <button className="modal-button modal-button--primary" onClick={handleCompleteProfile}>
                Let's Do It
              </button>
              <button className="modal-button modal-button--secondary" onClick={() => setShowCompleteModal(false)}>
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="dashboard-card">
        <UserTile currentUser={currentUser} userProfile={userProfile} editing={editing} />

        <div className="profile-kpis">
          <div className="profile-kpi">
            <span className="profile-kpi-value">{kpis.mealsHosted ?? '—'}</span>
            <span className="profile-kpi-label">Meals Hosted</span>
          </div>
          <div className="profile-kpi">
            <span className="profile-kpi-value">{kpis.mealsAttended ?? '—'}</span>
            <span className="profile-kpi-label">Meals Attended</span>
          </div>
          <div className="profile-kpi">
            <span className="profile-kpi-value">
              {kpis.hostRating != null ? `★ ${kpis.hostRating.toFixed(1)}` : '—'}
            </span>
            <span className="profile-kpi-label">Avg Host Rating</span>
          </div>
          <div className="profile-kpi">
            <span className="profile-kpi-value">
              {kpis.guestRating != null ? `★ ${kpis.guestRating.toFixed(1)}` : '—'}
            </span>
            <span className="profile-kpi-label">Avg Guest Rating</span>
          </div>
        </div>

        <UpdateProfile
          currentUser={currentUser}
          userProfile={userProfile}
          onProfileUpdate={setUserProfile}
          editing={editing}
          setEditing={setEditing}
        />

        <button className="sign-out-button" onClick={() => signOut(auth)}>
          Sign Out
        </button>
      </div>
    </div>
  );
};

export default Profile;
